import React, { useState, useEffect } from 'react';
import InvoiceDialog from './InvoiceDialog';
import { useUserData } from '../../../context/UserDataProvider';

import CancelConfirmDialog from './CancelConfirmDialog';
import { Box, Flex, Skeleton, Stack, Text, useDisclosure, useToast } from '@chakra-ui/react';
import CurrentSub from './CurrentSub';
import ZaraButton from '../../custom-components/buttons/Button';
import AddMinutes from '../../modals/AddMinutes';
import AutoRechargeDisplay from './AutoRechargeDisplay';
import { useError } from '../../../error-provider/ErrorProvider';
import useServerApi from 'hooks/useServerApi';
import useAvailablePlans from 'hooks/useAvailablePlans';
import { format } from 'date-fns';
import { PATHS } from '../../../router/routes';
import { useNavigate } from 'react-router-dom';



export const MANAGE_TYPE = {
    UPDATE: 'update',
    CANCEL: 'cancel',
};

const SubscriptionComponent = () => {
    // State for CurrentSub parameters derived from available plans
    const [currentSubParams, setCurrentSubParams] = useState({});
    const { allProducts, subscriptions, loading: plansLoading } = useAvailablePlans();
    const { userData: userDataFromContext, fetchUserData } = useUserData();
    const stripeSubscriptionId = userDataFromContext?.db_subscription_information?.stripe_subscription_id;
    const stripeSubscriptionInfo = userDataFromContext?.db_subscription_information;
    const router = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const { showError } = useError();

    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [mode, setMode] = useState('buy');
    const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);

    const [{ post, get }] = useServerApi();

    const [billDetails, setBillDetails] = useState(null);
    const [minutesRemaining, setMinutesRemaining] = useState(null);
    const [costPerMinute, setCostPerMinute] = useState(null);
    const pageLoading = plansLoading;
    const handleAutoRechargeSave = async (values) => {
        const [success,] = await post('/rest_endpoints/subscriptions/minutes/update', values)
        if (success) {
            setBillDetails(prev => ({
                ...prev,
                ...values,
            }));
            // Optionally show a toast or feedback
            toast({
                title: 'Auto recharge settings saved',
                status: 'success',
                isClosable: true,
            });
            return true;
        }
        toast({
            title: 'Failed to save auto recharge settings',
            status: 'error',
            isClosable: true,
        });
    };



    useEffect(() => {
        fetchBillDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const nextBillingDate = stripeSubscriptionInfo?.current_period_end;
    const nextBillingAmount = stripeSubscriptionInfo?.price;
    const status = stripeSubscriptionInfo?.status;
    const cancel = stripeSubscriptionInfo?.cancel_at_period_end;
    const isCancelled = status === 'canceled' || cancel;
    const trialEnd = stripeSubscriptionInfo?.trial_period_end;
    const isTrial = status === 'trialing';

    const fetchBillDetails = async () => {
        try {
            const [success, res] = await get(`/rest_endpoints/subscriptions/minutes`);
            if (success && res) {
                setBillDetails(res);
                setMode(res.auto_recharge_enabled ? 'auto' : 'buy');
                const minutes = Number(res.minutes) || 0;
                const extraMinutes = Number(res.extra_minutes) || 0;
                setMinutesRemaining(Math.floor(minutes + extraMinutes));
            } else {
                setBillDetails(null);
                setMinutesRemaining(null);
            }

            // Fetch cost per minute
            const [costSuccess, costRes] = await get(`/rest_endpoints/subscriptions/cost_per_minute`);
            if (costSuccess && costRes) {
                setCostPerMinute(costRes.cost_per_minute);
            } else {
                setCostPerMinute(null);
            }
        } catch (error) {
            setBillDetails(null);
            setMinutesRemaining(null);
            setCostPerMinute(null);
            showError(undefined, error);
        }
    };

    useEffect(() => {
        if (allProducts.length > 0 && subscriptions.length > 0) {
            const subscriptionProduct = subscriptions[0].product;
            allProducts.forEach(product => {
                if (product.id === subscriptionProduct) {
                    setCurrentSubParams({
                        planName: product.name,
                        priceAmount: product.priceAmount,
                        features: product.features,
                    });
                }
            });
        }
    }, [allProducts, subscriptions]);

    const onAddMinutesClose = () => {
        fetchBillDetails();
        onClose();
    };

    const onCancelClick = () => {
        if (!stripeSubscriptionId) {
            toast({
                title: 'Stripe subscription ID not found',
                status: 'error',
                isClosable: true,
            });
            return;
        }
        setShowCancelDialog(true);
        setCancelLoading(true);
    };

    // Dummy handlers for dialog
    const handleCancelConfirm = async () => {
        setShowCancelDialog(false);
        // Add actual cancel logic here
        console.log('Subscription cancellation confirmed');
        await fetchUserData();
        setCancelLoading(false);
    };

    const handleCancelDialogClose = () => {
        setShowCancelDialog(false);
        console.log('Subscription cancellation aborted');
        setCancelLoading(false);
    };

    const onManagePlan = () => {
        setShowInvoiceDialog(true);
    };
    const onUpgradePlan = () => {
        router(PATHS.UPGRADE_PLAN);
    }

    return (
        <>
            {showCancelDialog && (
                <CancelConfirmDialog
                    onConfirm={handleCancelConfirm}
                    onCancel={handleCancelDialogClose}
                    subscriptionId={stripeSubscriptionId}
                />
            )}
            <InvoiceDialog open={showInvoiceDialog} onClose={() => setShowInvoiceDialog(false)} />
            <Flex
                boxShadow=' 0px 1px 3px 0px #0000001A'
                alignItems='center'
                justifyContent='center'
                mt='6'
                w='100%'
                css={{
                    '@media only screen and (max-width: 920px)': {
                        marginLeft: '0px',
                    },
                    '@media only screen and (min-width: 500px)': {
                        display: 'flex',
                    },
                    '@media only screen and (max-width: 717px)': {
                        display: 'none',
                    },
                }}
                border='1px solid #E2E8F0'
                borderRadius='4px'
                p='40px 10px'
            >
                <Stack w='100%'>
                    <Flex w='100%' justifyContent='space-evenly'>
                        <Stack alignItems='center'>
                            <Skeleton isLoaded={!pageLoading}>
                                {/* New code: uses derived state from availablePlans */}
                                {!pageLoading && (<CurrentSub
                                    subLoading={pageLoading}
                                    planName={currentSubParams.planName}
                                    features={currentSubParams.features}
                                    priceAmount={currentSubParams.priceAmount}
                                />)}
                            </Skeleton>
                            <Skeleton w='100%' isLoaded={!pageLoading}>
                                {!cancel && (
                                    <ZaraButton
                                        w='100%'
                                        isLoading={pageLoading}
                                        isDisabled={pageLoading}
                                        onClick={() => {
                                            onUpgradePlan();
                                        }}
                                        variant='successOutline'
                                    >
                                        Upgrade Plan
                                    </ZaraButton>
                                )}
                            </Skeleton>
                        </Stack>

                        <Stack justifyContent='space-between'>
                            <Skeleton w='100%' isLoaded={!pageLoading}>
                                <Box border='1px solid #E2E8F0' borderRadius='6px'>
                                    <Text mt='20px' textAlign='center'>
                                        Minutes Remaining This Billing Period: <strong>{minutesRemaining !== null ? minutesRemaining : '-'}</strong>
                                    </Text>
                                    {billDetails && (<AutoRechargeDisplay
                                        billDetails={billDetails}
                                        onSave={handleAutoRechargeSave}
                                        mode={mode}
                                        setMode={setMode}
                                    />)}
                                    <Box textAlign='center'>
                                        {mode === 'buy' && (
                                            <ZaraButton onClick={onOpen} mt='16px' w='80%' mb='20px'>
                                                + Buy More Minutes
                                            </ZaraButton>
                                        )}
                                    </Box>
                                </Box>
                            </Skeleton>


                            <Skeleton w='100%' isLoaded={!pageLoading}>
                                <Box p='22px 8px' border='1px solid #E2E8F0' borderRadius='6px'>
                                    <Text
                                        fontSize='14px'
                                        mb='20px'
                                        fontWeight='bold'
                                        color={isCancelled ? 'red.500' : 'black'}
                                    >
                                        {isTrial
                                            ? 'Your trial period is valid till '
                                            : isCancelled
                                                ? 'Your subscription will be concluding on: '
                                                : 'Next billing date: '}
                                        {status !== cancel &&
                                            format(new Date(isTrial ? trialEnd : nextBillingDate), 'MM/dd/yyyy')}
                                    </Text>

                                    {isCancelled ? (
                                        <>
                                            <Text fontSize='14px'>
                                                We appreciate your support and hope that you have found value in our
                                                services.
                                            </Text>
                                            <Text fontSize='14px'>
                                                If you have any questions or need assistance regarding your
                                                subscription, please don’t hesitate to reach out to our dedicated
                                                support team
                                                <Text
                                                    ml='5px'
                                                    display='inline'
                                                    color='#3182CE'
                                                    cursor='pointer'
                                                    _hover={{
                                                        textDecor: 'underline',
                                                    }}
                                                >
                                                    contact@dialzara.com
                                                </Text>
                                            </Text>
                                        </>
                                    ) : (
                                        <>
                                            <Text>Subscription renews automatically.</Text>
                                            <Text>
                                                {isTrial ? (
                                                    'Subscription charge will be at '
                                                ) : (
                                                    <Text display='inline'>
                                                        The next charge $
                                                        <Text as='span' minW='20px'>
                                                            {nextBillingAmount ?? ''}
                                                        </Text>
                                                        {' will occur on '}
                                                    </Text>
                                                )}
                                                {status !== cancel &&
                                                    format(
                                                        new Date(isTrial ? trialEnd : nextBillingDate),
                                                        'MMMM, dd yyyy'
                                                    )}
                                                .
                                            </Text>
                                            <Text>For all questions related to payments contact us </Text>
                                            <Text
                                                color='#3182CE'
                                                cursor='pointer'
                                                _hover={{
                                                    textDecor: 'underline',
                                                }}
                                            >
                                                contact@dialzara.com
                                            </Text>
                                        </>
                                    )}
                                </Box>
                            </Skeleton>
                            <Flex alignItems='center' justifyContent='space-between' w='100%'>
                                <Skeleton w='100%' isLoaded={!pageLoading}>
                                    {isCancelled ? (
                                        <Flex justifyContent='flex-end' w='100%'>
                                            <Flex justifyContent='center'>
                                                <ZaraButton
                                                    isLoading={pageLoading}
                                                    isDisabled={pageLoading}
                                                    onClick={() => {
                                                        onManagePlan();
                                                    }}
                                                >
                                                    Manage Plan
                                                </ZaraButton>
                                            </Flex>
                                        </Flex>
                                    ) : (
                                        <Flex justifyContent='space-between'>
                                            <ZaraButton
                                                isLoading={pageLoading}
                                                isDisabled={pageLoading}
                                                onClick={() => {
                                                    onManagePlan();
                                                }}
                                                w='150px'
                                            >
                                                Manage Plan
                                            </ZaraButton>
                                            <ZaraButton
                                                isLoading={cancelLoading}
                                                isDisabled={cancelLoading}
                                                onClick={onCancelClick}
                                                variant='errorOutline'
                                                w='150px'
                                            >
                                                Cancel Plan
                                            </ZaraButton>
                                        </Flex>
                                    )}
                                </Skeleton>
                            </Flex>
                        </Stack>
                    </Flex>
                </Stack>
            </Flex>

            <Box
                mb='100px'
                css={{
                    '@media only screen and (max-width: 717px)': {
                        display: 'block',
                    },
                    '@media only screen and (min-width: 718px)': {
                        display: 'none',
                    },
                    '@media only screen and (max-width: 450px)': {
                        display: 'none',
                    },
                }}
                w='100%'
                mt='110px'
            >
                <Flex flexDir='column' justifyContent='center' alignItems='center' w='100%'>
                    <Box w='75%'>
                        <Box mb='10px'>
                            {/* New code: uses derived state from availablePlans */}
                            <CurrentSub
                                subLoading={pageLoading}
                                planName={currentSubParams.planName}
                                features={currentSubParams.features}
                                priceAmount={currentSubParams.priceAmount}
                            />
                        </Box>
                        <ZaraButton
                            mb='40px'
                            w='100%'
                            isLoading={pageLoading}
                            isDisabled={pageLoading}
                            onClick={() => {
                                onUpgradePlan();
                            }}
                            variant='successOutline'
                        >
                            Upgrade Plan
                        </ZaraButton>
                        <Box mb='10px' p='30px 15px' border='1px solid #E2E8F0' borderRadius='12px'>
                            <Text>
                                Minutes Remaining This Billing Period:{' '}
                                <Skeleton display='inline' isLoaded={!pageLoading}>
                                    <strong>{minutesRemaining !== null ? minutesRemaining : '-'}</strong>
                                </Skeleton>
                            </Text>
                            {billDetails && (
                                <AutoRechargeDisplay
                                    billDetails={billDetails}
                                    onSave={handleAutoRechargeSave}
                                    mode={mode}
                                    setMode={setMode}
                                />
                            )}
                        </Box>
                        {mode === 'buy' && (
                            <ZaraButton onClick={onOpen} w='100%' mb='40px'>
                                + Buy More Minutes
                            </ZaraButton>
                        )}
                        <Box mb='10px' p='30px 15px' border='1px solid #E2E8F0' borderRadius='12px'>
                            <Box w='100%' maxW='285px'>
                                <Text fontSize='16px' mb='20px' fontWeight='bold' color={cancel ? 'red.500' : 'black'}>
                                    {status === 'trialing'
                                        ? 'Your trial period is valid till '
                                        : cancel
                                            ? 'Your subscription will be concluding on: '
                                            : 'Next billing date: '}
                                    <Skeleton display='inline' isLoaded={!pageLoading}>
                                        {status !== cancel &&
                                            format(
                                                new Date(status === 'trialing' ? trialEnd : nextBillingDate),
                                                'MM/dd/yyyy'
                                            )}
                                    </Skeleton>
                                </Text>
                                {cancel ? (
                                    <>
                                        <Text fontSize='16px'>
                                            We appreciate your support and hope that you have found value in our
                                            services.
                                        </Text>
                                        <Text fontSize='16px'>
                                            If you have any questions or need assistance regarding your subscription,
                                            please don’t hesitate to reach out to our dedicated support team
                                            <Text
                                                ml='5px'
                                                display='inline'
                                                color='#3182CE'
                                                cursor='pointer'
                                                _hover={{
                                                    textDecor: 'underline',
                                                }}
                                            >
                                                contact@dialzara.com
                                            </Text>
                                        </Text>
                                    </>
                                ) : (
                                    <>
                                        <Text>Subscription renews automatically.</Text>
                                        <Text>
                                            {trialEnd ? (
                                                'Subscript charge will be at '
                                            ) : (
                                                <Text display='inline'>
                                                    {'The next charge '}
                                                    <Text display='inline-block' fontWeight='bold'>
                                                        $
                                                    </Text>
                                                    <Skeleton display='inline' isLoaded={!pageLoading}>
                                                        <Text as='span' minW='20px'>
                                                            {nextBillingAmount ?? ''}
                                                        </Text>
                                                    </Skeleton>
                                                    {' will occur on '}
                                                </Text>
                                            )}
                                            <Text display='inline-block' fontWeight='bold'>
                                                {status !== cancel &&
                                                    format(
                                                        new Date(status === 'trialing' ? trialEnd : nextBillingDate),
                                                        'MMMM, dd yyyy'
                                                    )}
                                            </Text>
                                        </Text>
                                        <Text>
                                            {'For all questions related to payments contact us '}
                                            <Text
                                                display='inline'
                                                color='#3182CE'
                                                cursor='pointer'
                                                _hover={{
                                                    textDecor: 'underline',
                                                }}
                                            >
                                                contact@dialzara.com
                                            </Text>
                                        </Text>
                                    </>
                                )}
                            </Box>
                        </Box>
                        <ZaraButton
                            mb='10px'
                            w='100%'
                            isLoading={pageLoading}
                            isDisabled={pageLoading}
                            onClick={() => {
                                onManagePlan();
                            }}
                        >
                            Manage Plan
                        </ZaraButton>
                        <ZaraButton
                            isLoading={cancelLoading}
                            isDisabled={cancelLoading}
                            onClick={onCancelClick}
                            variant='errorOutline'
                            w='100%'
                        >
                            Cancel Plan
                        </ZaraButton>
                    </Box>
                </Flex>
            </Box>

            {/*Mobile version */}
            <Box
                mb='100px'
                css={{
                    '@media only screen and (max-width: 450px)': {
                        display: 'block',
                    },
                    '@media only screen and (min-width: 500px)': {
                        display: 'none',
                    },
                }}
                w='100%'
                mt='70px'
            >
                <Box mb='10px'>

                    {/* New code: uses derived state from availablePlans */}
                    <CurrentSub
                        subLoading={pageLoading}
                        planName={currentSubParams.planName}
                        features={currentSubParams.features}
                        priceAmount={currentSubParams.priceAmount}
                    />
                </Box>
                <ZaraButton
                    mb='40px'
                    w='100%'
                    isLoading={pageLoading}
                    isDisabled={pageLoading}
                    onClick={() => {
                        onUpgradePlan();
                    }}
                    variant='successOutline'
                >
                    Upgrade Plan
                </ZaraButton>
                <Box mb='10px' p='30px 15px' border='1px solid #E2E8F0' borderRadius='12px'>
                    <Text>
                        Minutes Remaining This Billing Period:{' '}
                        <Skeleton display='inline' isLoaded={!pageLoading}>
                            <strong>{minutesRemaining !== null ? minutesRemaining : '-'}</strong>
                        </Skeleton>
                    </Text>
                    {billDetails && (
                        <AutoRechargeDisplay
                            billDetails={billDetails}
                            onSave={handleAutoRechargeSave}
                            mode={mode}
                            setMode={setMode}
                        />
                    )}
                </Box>
                {mode === 'buy' && (
                    <ZaraButton onClick={onOpen} w='100%' mb='40px'>
                        + Buy More Minutes
                    </ZaraButton>
                )}
                <Box mb='10px' p='30px 15px' border='1px solid #E2E8F0' borderRadius='12px'>
                    <Box w='100%' maxW='285px'>
                        <Text fontSize='16px' mb='20px' fontWeight='bold' color={cancel ? 'red.500' : 'black'}>
                            {status === 'trialing'
                                ? 'Your trial period is valid till '
                                : cancel
                                    ? 'Your subscription will be concluding on: '
                                    : 'Next billing date: '}
                            <Skeleton display='inline' isLoaded={!pageLoading}>
                                {status !== cancel &&
                                    format(new Date(status === 'trialing' ? trialEnd : nextBillingDate), 'MM/dd/yyyy')}
                            </Skeleton>
                        </Text>
                        {cancel ? (
                            <>
                                <Text fontSize='16px'>
                                    We appreciate your support and hope that you have found value in our services.
                                </Text>
                                <Text fontSize='16px'>
                                    If you have any questions or need assistance regarding your subscription, please
                                    don’t hesitate to reach out to our dedicated support team
                                    <Text
                                        ml='5px'
                                        display='inline'
                                        color='#3182CE'
                                        cursor='pointer'
                                        _hover={{
                                            textDecor: 'underline',
                                        }}
                                    >
                                        contact@dialzara.com
                                    </Text>
                                </Text>
                            </>
                        ) : (
                            <>
                                <Text>Subscription renews automatically.</Text>
                                <Text>
                                    {trialEnd ? (
                                        'Subscript charge will be at '
                                    ) : (
                                        <Text display='inline'>
                                            {'The next charge '}
                                            <Text display='inline-block' fontWeight='bold'>
                                                $
                                            </Text>
                                            <Skeleton display='inline' isLoaded={!pageLoading}>
                                                <Box display='inline-block' minW='20px'>
                                                    {nextBillingAmount ?? ''}
                                                </Box>
                                            </Skeleton>
                                            {' will occur on '}
                                        </Text>
                                    )}
                                    <Text display='inline-block' fontWeight='bold'>
                                        {status !== cancel &&
                                            format(
                                                new Date(status === 'trialing' ? trialEnd : nextBillingDate),
                                                'MMMM, dd yyyy'
                                            )}
                                    </Text>
                                </Text>
                                <Text>
                                    {'For all questions related to payments contact us '}
                                    <Text
                                        display='inline'
                                        color='#3182CE'
                                        cursor='pointer'
                                        _hover={{
                                            textDecor: 'underline',
                                        }}
                                    >
                                        contact@dialzara.com
                                    </Text>
                                </Text>
                            </>
                        )}
                    </Box>
                </Box>
                <ZaraButton
                    mb='10px'
                    w='100%'
                    isLoading={pageLoading}
                    isDisabled={pageLoading}
                    onClick={() => {
                        onManagePlan();
                    }}
                >
                    Manage Plan
                </ZaraButton>
                <ZaraButton
                    isLoading={cancelLoading}
                    isDisabled={cancelLoading}
                    onClick={onCancelClick}
                    variant='errorOutline'
                    w='100%'
                >
                    Cancel Plan
                </ZaraButton>
            </Box>

            {/* Add the modal for all views */}
            <AddMinutes isTrial={isTrial} costPerMinute={costPerMinute} onClose={onAddMinutesClose} isOpen={isOpen} />
        </>
    );
};

export default SubscriptionComponent;
